/**
 * Журнал сбоев на устройстве.
 *
 * До него о любом сбое можно было узнать только из жалобы человека: три вызова
 * `console` на весь проект, и те уходят в консоль, которую на телефоне никто не
 * открывает. Разбор превращался в переписку «а что вы нажали» — и однажды уже
 * стоил нескольких неверных диагнозов подряд.
 *
 * Журнал живёт на устройстве и никуда сам не уходит. Это осознанно: приложение
 * ведёт записи о здоровье, и отправлять их обломки на чужой сервер без ведома
 * человека нельзя. Поделиться журналом можно только явным действием — кнопкой.
 *
 * Хранится последняя сотня событий: этого хватает, чтобы восстановить, что
 * происходило перед сбоем, и мало, чтобы заметно занять место.
 */

import { db } from '@/shared/db/database';

/** Сколько событий держим. Старые вытесняются новыми. */
export const DIAGNOSTICS_LIMIT = 100;

/** Ключ в таблице настроек устройства: журнал не принадлежит аккаунту. */
const STORAGE_KEY = 'diagnostics.log';

export type DiagnosticLevel = 'error' | 'warn' | 'info';

export interface DiagnosticEvent {
  at: number;
  level: DiagnosticLevel;
  /** Где произошло: экран, модуль, действие. */
  area: string;
  message: string;
}

/**
 * Максимальная длина сообщения.
 *
 * Ошибки Dexie и браузера иногда несут в тексте значение ключа — то есть
 * кусочек данных человека. Обрезка не защита от этого, но она ограничивает,
 * сколько лишнего может осесть в журнале.
 */
const MAX_MESSAGE = 200;

/**
 * Приводит любую ошибку к одной строке.
 *
 * Стек не сохраняется: на устройстве он всё равно из сжатого кода и ничего не
 * объясняет, а места занимает много. Имя ошибки при этом сохраняется — по нему
 * отличается «база не открылась» от «поле не найдено».
 */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    const name = error.name && error.name !== 'Error' ? `${error.name}: ` : '';
    return `${name}${error.message}`.slice(0, MAX_MESSAGE);
  }

  if (typeof error === 'string') return error.slice(0, MAX_MESSAGE);
  return String(error).slice(0, MAX_MESSAGE);
}

/** Добавляет событие к журналу, вытесняя самые старые. */
export function appendEvent(
  log: readonly DiagnosticEvent[],
  event: DiagnosticEvent,
  limit: number = DIAGNOSTICS_LIMIT,
): DiagnosticEvent[] {
  return [...log, event].slice(-limit);
}

/**
 * Журнал в памяти.
 *
 * Запись в базу асинхронная, а сбой может случиться в любой момент, поэтому
 * событие сначала попадает сюда, а на диск уезжает следом. Так последнее
 * событие перед падением не теряется, даже если запись не успела пройти.
 */
let memoryLog: DiagnosticEvent[] = [];
let loaded = false;

export async function loadDiagnostics(): Promise<DiagnosticEvent[]> {
  if (loaded) return memoryLog;

  try {
    const row = await db.settings.get(STORAGE_KEY);
    memoryLog = Array.isArray(row?.value) ? (row.value as DiagnosticEvent[]) : [];
  } catch {
    // Журнал не работает без базы, но ронять из-за этого приложение нельзя:
    // он существует ровно для тех случаев, когда что-то уже сломалось.
    memoryLog = [];
  }

  loaded = true;
  return memoryLog;
}

/**
 * Записывает событие.
 *
 * Ничего не выбрасывает и ничего не ждёт: вызывающий код — это обработчик
 * сбоя, и вторая ошибка внутри него была бы худшим, что можно придумать.
 */
export function record(level: DiagnosticLevel, area: string, error: unknown): void {
  const event: DiagnosticEvent = {
    at: Date.now(),
    level,
    area,
    message: describeError(error),
  };

  memoryLog = appendEvent(memoryLog, event);

  void (async () => {
    try {
      await db.settings.put({ key: STORAGE_KEY, value: memoryLog, updatedAt: event.at });
    } catch {
      // Не записалось — событие всё равно осталось в памяти до перезагрузки.
    }
  })();
}

/** Текущее содержимое журнала, свежие события в конце. */
export function currentDiagnostics(): DiagnosticEvent[] {
  return memoryLog;
}

export async function clearDiagnostics(): Promise<void> {
  memoryLog = [];
  try {
    await db.settings.delete(STORAGE_KEY);
  } catch {
    // Ничего страшного: в памяти уже пусто.
  }
}

/**
 * Журнал одним текстом — чтобы человек мог его переслать.
 *
 * Формат нарочно простой: время, уровень, место, сообщение. Такое читается и
 * в мессенджере, и в письме, без вложений и без специальных программ.
 */
export function formatDiagnostics(
  log: readonly DiagnosticEvent[] = memoryLog,
  meta: { version?: string; platform?: string } = {},
): string {
  const header = [
    'Диагностика Kairo',
    meta.version ? `Версия: ${meta.version}` : null,
    meta.platform ? `Среда: ${meta.platform}` : null,
    `Событий: ${log.length}`,
  ].filter(Boolean);

  const lines = log.map((event) => {
    const time = new Date(event.at).toLocaleString('ru-RU');
    return `${time} · ${event.level} · ${event.area} · ${event.message}`;
  });

  return [...header, '', ...lines].join('\n');
}

/**
 * Подписывает глобальные обработчики.
 *
 * Необработанное исключение и отклонённый промис — это ровно те сбои, о
 * которых иначе не остаётся никакого следа: границы ошибок ловят только рендер.
 */
export function watchGlobalErrors(): () => void {
  const onError = (event: ErrorEvent) => record('error', 'window', event.error ?? event.message);
  const onRejection = (event: PromiseRejectionEvent) => record('error', 'promise', event.reason);

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
